import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { sendContactData } from './services/contactService';
import './AIChatBot.css';

// Компонент для форматирования текста (жирный, ссылки, переносы)
const ChatMessageContent = ({ text }) => {
    const createMarkup = () => {
        let formattedText = text
            // 1. Сначала обрабатываем Markdown-ссылки [текст](url)
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
            // 2. Затем жирный текст **текст**
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // 3. Потом голые URL (которые не в Markdown-ссылках)
            .replace(/(?<!href=["'])(?<!>)(https?:\/\/[^\s<]+)(?![^<]*<\/a>)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
            // 4. И наконец переносы строк
            .replace(/\n/g, '<br />');
        return { __html: formattedText };
    };
    return <div dangerouslySetInnerHTML={createMarkup()} />;
};

// ======================================================================
// === ПОЛНОСТЬЮ ПЕРЕПИСАННЫЙ КОМПОНЕНТ ФОРМЫ С МАСКОЙ И ВАЛИДАЦИЕЙ ===
// ======================================================================
const ChatForm = ({ onSubmit, t }) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    // Функция для форматирования номера в маску +7 (XXX) XXX-XX-XX
    const formatPhoneNumber = (value) => {
        if (!value) return "";
        // Оставляем только цифры
        const phoneNumber = value.replace(/[^\d]/g, '');

        // Убираем первую цифру, если это 7 или 8
        let number = phoneNumber;
        if (phoneNumber.startsWith('7') || phoneNumber.startsWith('8')) {
            number = phoneNumber.slice(1);
        }

        // Ограничиваем до 10 цифр
        number = number.slice(0, 10);

        let formatted = '+7 (';
        if (number.length > 0) {
            formatted += number.slice(0, 3);
        }
        if (number.length >= 4) {
            formatted += `) ${number.slice(3, 6)}`;
        }
        if (number.length >= 7) {
            formatted += `-${number.slice(6, 8)}`;
        }
        if (number.length >= 9) {
            formatted += `-${number.slice(8, 10)}`;
        }

        return formatted;
    };

    const handlePhoneChange = (e) => {
        const formattedPhoneNumber = formatPhoneNumber(e.target.value);
        setPhone(formattedPhoneNumber);

        // Проверяем, что номер полностью введен (18 символов с маской)
        if (formattedPhoneNumber.length === 18) {
            setIsPhoneValid(true);
        } else {
            setIsPhoneValid(false);
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (name.trim() && isPhoneValid) {
            onSubmit(name, phone);
        }
    };

    return (
        <form onSubmit={handleFormSubmit} className="chat-form">
            <p>{t('chatFormIntro')}</p>
            <div className="form-group">
                <label>{t('chatFormNameLabel')}</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('productModalNamePlaceholder')}
                    required
                />
            </div>
            <div className="form-group">
                <label>{t('chatFormPhoneLabel')}</label>
                <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="+7 (___) ___-__-__"
                    className={phone.length > 0 ? (isPhoneValid ? 'phone-valid' : 'phone-invalid') : ''}
                    required
                />
                {phone.length > 0 && !isPhoneValid &&
                    <small className="error-message">{t('chatPhoneInvalid')}</small>
                }
            </div>
            <button
                type="submit"
                className="form-submit-btn"
                disabled={!name.trim() || !isPhoneValid} // Кнопка активна, только если все поля заполнены
            >
                {t('chatFormSubmitButton')}
            </button>
        </form>
    );
};
// ======================================================================


// Функция для генерации ID сессии
const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};


const AIChatBot = () => {
    const { t, language } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const [sessionId, setSessionId] = useState('');
    const [userRegistered, setUserRegistered] = useState(false);

    // Установка ID сессии
    useEffect(() => {
        let currentSessionId = sessionStorage.getItem('ai_chat_session_id');
        if (!currentSessionId) {
            currentSessionId = generateSessionId();
            sessionStorage.setItem('ai_chat_session_id', currentSessionId);
        }
        setSessionId(currentSessionId);
    }, []);

    // Проверка, зарегистрирован ли пользователь
    useEffect(() => {
        const registered = sessionStorage.getItem('ai_chat_user_registered');
        if (registered) {
            setUserRegistered(true);
        }
    }, []);

    // Автоматическая прокрутка вниз
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };
    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    // Очищаем класс при размонтировании
    useEffect(() => {
        return () => {
            document.body.classList.remove('chat-open');
        };
    }, []);


    const sendToWebhook = async (userInput, userName) => {
        const webhookURL = 'https://stroy-m.kz/webhook/aison_site';
        const errorMessage = t('errorMessage', 'Извините, сейчас не могу ответить. Пожалуйста, попробуйте позже.');

        try {
            const payload = {
                message: userInput,
                userName: userName || "Клиент",
                language: language,
                timestamp: new Date().toISOString(),
                sessionId: sessionId
            };
            const response = await fetch(webhookURL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) { return errorMessage; }

            const responseText = await response.text();
            try {
                const aiResponse = JSON.parse(responseText);
                return aiResponse.message || JSON.stringify(aiResponse);
            } catch (jsonError) {
                return responseText;
            }
        } catch (error) {
            return errorMessage;
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        const userMessage = { id: Date.now(), text: inputValue, sender: 'user' };
        setMessages(prev => [...prev, userMessage]);
        const currentInput = inputValue;
        setInputValue('');

        if (!userRegistered) {
            return;
        }

        setIsTyping(true);
        const botResponse = await sendToWebhook(currentInput);
        const botMessage = { id: Date.now() + 1, text: botResponse, sender: 'bot' };
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
    };

    const handleFormSubmit = (name, phone) => {
        const contactData = {
            name: name,
            phone: phone,
            source: 'ai-chat-form',
            language: language,
        };

        // Отправляем контактные данные в фоновом режиме (не ждем)
        sendContactData(contactData)
            .then(() => {
                // Контактные данные из чата отправлены в Firestore
            })
            .catch(error => { });

        // --- МГНОВЕННАЯ РЕАКЦИЯ ДЛЯ ПОЛЬЗОВАТЕЛЯ ---

        // 1. Сразу регистрируем пользователя
        setUserRegistered(true);
        sessionStorage.setItem('ai_chat_user_registered', 'true');
        sessionStorage.setItem('ai_chat_user_name', name);

        // 2. Сразу показываем сообщение об успехе
        const successMessage = t('chatFormSuccess');
        const successBotMessage = { id: Date.now(), text: successMessage, sender: 'bot' };

        const firstUserMessage = messages.find(m => m.sender === 'user');

        if (firstUserMessage) {
            // 3. Сразу показываем сообщение об успехе
            setMessages([successBotMessage]);
            setIsTyping(true);

            // 4. Асинхронно (в фоне) отправляем запрос к AI
            sendToWebhook(firstUserMessage.text, name).then(botResponse => {
                const botResponseMessage = { id: Date.now() + 1, text: botResponse, sender: 'bot' };
                // 5. Когда ответ готов, заменяем индикатор печати на ответ
                setIsTyping(false);
                setMessages(prev => [...prev, botResponseMessage]);
            });
        } else {
            // Если пользователь ничего не писал, просто благодарим
            setMessages([successBotMessage]);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);

        // Добавляем/убираем класс к body для скрытия шапки
        if (!isOpen) {
            document.body.classList.add('chat-open');
        } else {
            document.body.classList.remove('chat-open');
        }
    };

    const showForm = !userRegistered && messages.some(m => m.sender === 'user');

    return (
        <div className="ai-chatbot-container">
            {!isOpen && (
                <button className="chat-toggle-btn" onClick={toggleChat}>
                    <span className="chat-icon">💬</span>
                    <span className="chat-text">{t('aiAssistant', 'AI Помощник')}</span>
                </button>
            )}

            {isOpen && (
                <div className="chatbot-widget">
                    <div className="chatbot-header">
                        <span className="chatbot-title">🤖 {t('aiAssistant', 'AI Помощник')}</span>
                        <button className="close-chat-btn" onClick={toggleChat}>✕</button>
                    </div>

                    <div className="chat-messages">
                        {messages.length === 0 && !userRegistered && (
                            <div className="chat-instruction">
                                <ChatMessageContent text={t('chatWelcome')} />
                                <hr />
                                <ChatMessageContent text={t('chatInstruction')} />
                            </div>
                        )}

                        {messages.map((message) => (
                            <div key={message.id} className={`message ${message.sender}`}>
                                <div className="message-bubble"><ChatMessageContent text={message.text} /></div>
                            </div>
                        ))}

                        {showForm && (
                            <div className="message bot">
                                <div className="message-bubble form-bubble">
                                    <ChatForm onSubmit={handleFormSubmit} t={t} />
                                </div>
                            </div>
                        )}

                        {isTyping && (
                            <div className="message bot">
                                <div className="message-bubble typing">
                                    <div className="typing-dots"><span></span><span></span><span></span></div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {!showForm && (
                        <div className="chat-input-container">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={t('askQuestionPlaceholder', 'Напишите ваш вопрос...')}
                                className="chat-input"
                            />
                            <button onClick={handleSendMessage} className="send-btn">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.00004 16.17L4.83004 12L3.41004 13.41L9.00004 19L21 7.00003L19.59 5.59003L9.00004 16.17Z" fill="currentColor" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AIChatBot;